import { InboundEmailDTO } from './InboundEmailDTO';
import {
  InventoryStatus,
  SalesOrderSource,
  StagedPurchaseOrderStatus,
  type InventoryValidationResult,
  type PendingLineItemChange,
  type PendingHeaderChange,
} from '@erp/shared-types';
import type {
  DiscountValidationResult as RpcDiscountValidationResult,
  CustomerSpecificDiscountVerificationStatus as RpcCustomerSpecificDiscountVerificationStatus,
} from '@erp/rpc';
import { DiscountStatus as RpcDiscountStatus } from '@erp/rpc';

// Re-export discount validation types from RPC for single source of truth
export type DiscountValidationResult = RpcDiscountValidationResult;
export type DiscountStatus = RpcDiscountStatus;
export const DiscountStatus = RpcDiscountStatus;

// Re-export customer-specific discount verification types
export type CustomerSpecificDiscountVerificationStatus =
  RpcCustomerSpecificDiscountVerificationStatus;

// Re-export pending change types from shared-types
export type {
  PendingLineItemChange,
  PendingHeaderChange,
  PendingChangeDecision,
  PendingChangeType,
} from '@erp/shared-types';

/**
 * Customer-specific discount verification tracking for a line item.
 * Stored in LineItem JSONB within StagedCustomerPurchaseOrder.
 */
export interface CustomerSpecificDiscountVerification {
  discountId: string;
  discountScopeId: string;
  endCustomerName: string;
  status: CustomerSpecificDiscountVerificationStatus;
  verifiedByUserId?: string;
  verifiedAt?: string; // ISO date string
  notes?: string;
}

// Re-export for backward compatibility with existing frontend code
export { SalesOrderSource };
export type SalesOrderDraftStatus = StagedPurchaseOrderStatus;
export const SalesOrderDraftStatus = StagedPurchaseOrderStatus;

export type ApprovalIssueType =
  | 'no_list_price'
  | 'unknown_inventory'
  | 'insufficient_inventory'
  | 'inactive_inventory'
  | 'customer_specific_discount_pending'
  | 'unmatched_product';

export interface ApprovalIssue {
  type: ApprovalIssueType;
  /** Whether this line item was excluded from the submitted order */
  excludedFromOrder: boolean;
  /** Human-readable details */
  details?: string;
  /** ISO timestamp of when the issue was recorded */
  recordedAt: string;
}

export interface Charge {
  id?: string;
  type: 'shipping' | 'handling' | 'tax' | 'discount' | 'other';
  description: string;
  amount: number;
  isPercentage: boolean; // If true, amount is a percentage; if false, it's a fixed amount
}

/**
 * A party address exactly as it arrived on an EDI `N1` loop.
 *
 * Mirrors the backend's `AddressSchema`: `DUNS` is the identifier sent under
 * qualifier 1/9, `locationCode` the raw code for any other qualifier (e.g.
 * Target's buyer-assigned 92 DC code). Every field is optional — partners send
 * sparse parties, and the matcher accepts either identifier.
 */
export interface EdiPartyAddress {
  name?: string;
  DUNS?: string;
  locationCode?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

// Re-export the inventory validation types from shared-types (single source
// of truth; the local copies drifted from the backend's definitions).
export { InventoryStatus };
export type { InventoryValidationResult };

export interface LineItem {
  // Basic line item information
  origin?: string;
  unit?: string;
  warehouse?: string;
  leadTimeDays?: number;
  pallets?: number;
  quantity?: number;
  unitPrice?: number;
  amount?: number;

  //deprecated
  sku?: string;
  itemName?: string;
  name?: string;
  upc?: string;

  // email fields
  customerDescription?: string;
  customerIdentifier?: string;

  // AI suggested values for accuracy tracking
  suggestedQuantity?: number;
  suggestedUnitPrice?: number;
  suggestedAmount?: number;
  suggestedCustomerDescription?: string;
  suggestedCustomerIdentifier?: string;

  // edi
  rescheduledDate?: string;
  ediRejected?: boolean;

  suggestedProductVariantId?: string;
  suggestedProductId?: string;
  selectedProductVariantId?: string;
  topProductVariantMatches?: Array<{
    productVariantId: string;
    confidence: number;
  }>;
  selectedCustomerProductId?: string;
  suggestedCustomerProductId?: string;
  /**
   * EDI (850/875) lines match against the customer's Famous translation list and
   * carry only this. It — not `selectedProductVariantId` — is what makes an EDI
   * line matched: the Famous product catalog syncs separately, so a correctly
   * mapped line often has no variant.
   */
  selectedTranslationListItemId?: string;

  // Discount validation
  discountValidation?: RpcDiscountValidationResult;

  // Inventory validation
  inventoryValidation?: InventoryValidationResult;

  // Line item charges
  charges?: Charge[];

  // Customer-specific discount verification tracking
  customerSpecificDiscountVerification?: CustomerSpecificDiscountVerification;

  // Populated at approval time when this line item has a soft issue
  approvalIssue?: ApprovalIssue;

  PO1_loop?: {
    [key: string]: unknown;
    baselineItemData: {
      quantity: string;
      unitPrice: string;
      productServiceID: string;
      productServiceID1: string;
      productServiceID2: string;
      productServiceID3: string;
      productServiceID4: string;
      productServiceID5: string;
      productServiceIDQualifier: string;
      productServiceIDQualifier1: string;
      productServiceIDQualifier2: string;
      productServiceIDQualifier3: string;
      productServiceIDQualifier4: string;
      productServiceIDQualifier5: string;
      unitOrBasisForMeasurementCode: string;
    };
  };
}

/** A structured processing error recorded on a draft (mirrors the backend). */
export interface ProcessingError {
  type: string;
  meta?: unknown;
}

export type {
  ErpErrorReport,
  ErpErrorReportGroup,
  ErpErrorReportItem,
} from '@erp/shared-types';

export interface SalesOrderDraft {
  id: string;
  organizationId: string;
  status: SalesOrderDraftStatus;
  /**
   * Structured processing errors on the draft. An `ERP_SYNC_FAILED` entry
   * carries `meta.message` — the **formatted report** when the adapter built
   * one, otherwise the ERP's raw text — plus the structured `ErpErrorReport` in
   * `meta.report`, whose `raw` field holds the ERP's own words. Set when status
   * is `ERP_SYNC_FAILED`.
   */
  processingErrors?: ProcessingError[];
  source: SalesOrderSource;
  sourceId: string;
  sourceData?: {
    email?: InboundEmailDTO;
    orderfulJson?: Record<string, unknown>;
    /**
     * Signed preview URL for the CORE_DOCUMENT that was OCR'd. When `source`
     * is `SalesOrderSource.Email`, this is set alongside `email` when the draft
     * came from a split child or attachment (`direct_source_document_id` on the
     * server) rather than the inbound body alone.
     */
    document?: {
      id: string;
      originalFileName: string;
      mimeType: string;
      fileSizeBytes: number;
      url: string;
    };
  };
  sourceEmail?: InboundEmailDTO;
  suggestedCustomerId?: string;
  suggestedCustomer?: {
    id: string;
    name: string;
    address?: string;
    phoneNumber?: string;
    emailDomain?: string;
    contactPerson?: string;
    creditLimit?: number;
  };
  suggestedCustomerRejected: boolean;
  customerId?: string;
  customer?: {
    id: string;
    name: string;
    erpDisplayName?: string | null;
    originId?: string | null;
    address?: string;
    phoneNumber?: string;
    emailDomain?: string;
    contactPerson?: string;
    creditLimit?: number;
  };
  suggestedCustomerCreationRejected: boolean;
  purchaseOrderNumber: string;
  receivedDate: Date | string;
  orderDate: Date | string;
  deliveryDate?: Date | string;
  shipDate?: Date | string;
  totalAmount?: string;
  extractedTotalAmount?: number;
  currency?: string;
  resolvedOrderType?: string | null;
  resolvedWarehouse?: string | null;
  modeOfTransport?: string | null;
  shippingMethod?: string | null;
  freightTerms?: string | null;
  freightCharges?: string | null;

  // Order-level charges
  charges?: Charge[];

  // Memory bank notes are now fetched via RPC (removed from DTO)

  destinationAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  /**
   * The bill-to / buying-party as the trading partner sent them on the 850's
   * `N1` loops. Informational: nothing downstream consumes them on the Famous
   * path, and they are not shown on drafts — see EDI_PARTY_NOT_SHOWN_NOTE in
   * `LocationPicker.tsx`.
   */
  billToAddress?: EdiPartyAddress;
  buyingPartyAddress?: EdiPartyAddress;
  lineItems?: Array<LineItem>;
  rawData: {
    extractedData: unknown;
    email?: {
      id: string;
      subject: string;
      from: string;
    };
    attachmentIds: string[];
  };
  shipToLocationId?: string;
  billToLocationId?: string;
  buyingPartyLocationId?: string;
  orderful850Payload?: Record<string, unknown>;
  orderful860Payloads?: Record<string, unknown>[];
  ediNotes?: EdiNote[] | null;
  pendingLineItemChanges?: PendingLineItemChange[] | null;
  pendingHeaderChanges?: PendingHeaderChange[] | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * A free-form note (N9 loop) sent on an inbound EDI 850.
 *
 * `text` holds the MSG/MTX lines under the N9 segment, `description` is N903,
 * and `lineNumber` is set only for notes attached to a specific PO1 line.
 */
export interface EdiNote {
  qualifier: string;
  referenceId?: string;
  description?: string;
  text: string[];
  lineNumber?: string;
}
